import { Button, Popconfirm, Tooltip } from 'antd';
import { Link } from 'react-router';
import type { UserListItem } from '../users.api';

interface UserActionsProps {
  user: UserListItem;
  /** Your own page: the server refuses self-deactivation, so the button explains why rather than failing. Resetting your own password has no such restriction. */
  isYou: boolean;
  /** Acting as someone needs the caller's own two-factor enrolment (the server refuses otherwise), so the button explains that up front. */
  hasTwoFactor: boolean;
  onResetPassword: () => void;
  /** Undefined unless the caller holds `user.impersonate`; the server refuses anyone else. */
  onActAs?: (user: UserListItem) => Promise<void>;
  onDeactivate: () => Promise<void>;
  onReactivate: () => Promise<void>;
}

function ActAsButton({ user, hasTwoFactor, onActAs }: { user: UserListItem; hasTwoFactor: boolean; onActAs: (user: UserListItem) => Promise<void> }) {
  if (!hasTwoFactor) {
    return (
      <Tooltip
        title={
          <>
            Turn on two-factor sign-in (authenticator app or email codes) in <Link to="/account/security">your security settings</Link> before acting as another
            user.
          </>
        }
      >
        {/* A disabled button swallows hover, so the tooltip hangs off a wrapper. */}
        <span>
          <Button disabled>Act as</Button>
        </span>
      </Tooltip>
    );
  }
  return (
    <Popconfirm
      title={`Act as ${user.name}?`}
      description="You'll see the app as they do. Your actions are recorded under your name."
      onConfirm={() => onActAs(user)}
    >
      <Button>Act as</Button>
    </Popconfirm>
  );
}

function StatusButton({ user, isYou, onDeactivate, onReactivate }: Pick<UserActionsProps, 'user' | 'isYou' | 'onDeactivate' | 'onReactivate'>) {
  if (isYou) {
    return (
      <Tooltip title="You can't deactivate your own account">
        <Button danger disabled>
          Deactivate
        </Button>
      </Tooltip>
    );
  }
  if (user.isActive) {
    return (
      <Popconfirm title={`Deactivate ${user.name}?`} description="They are signed out everywhere immediately." onConfirm={onDeactivate}>
        <Button danger>Deactivate</Button>
      </Popconfirm>
    );
  }
  return (
    <Popconfirm title={`Reactivate ${user.name}?`} onConfirm={onReactivate}>
      <Button>Reactivate</Button>
    </Popconfirm>
  );
}

/** Deactivation is immediate and affects someone else's account, so it asks first — same weight CLAUDE.md gives closing out a trip. */
export function UserActions({ user, isYou, hasTwoFactor, onResetPassword, onActAs, onDeactivate, onReactivate }: UserActionsProps) {
  const canActAs = onActAs !== undefined && !isYou && user.isActive && !user.roles.includes('admin');

  return (
    <span className="inline-flex flex-wrap gap-2">
      <Button onClick={onResetPassword}>Reset password</Button>
      {canActAs && <ActAsButton user={user} hasTwoFactor={hasTwoFactor} onActAs={onActAs} />}
      <StatusButton user={user} isYou={isYou} onDeactivate={onDeactivate} onReactivate={onReactivate} />
    </span>
  );
}
